import {ViewController} from "data/types/structure";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {inject, injectable} from "inversify";
import {action, makeAutoObservable, observable} from "mobx";
import {RequestState} from "data/enums";
import {Bindings} from "data/constants/bindings";
import type {IUserStore} from "data/stores/user/user.store";
import type {IContest, IContestsStore} from "data/stores/contests/contests.store";
import type {IPlayersStore} from "data/stores/players/players.store";
import type {ISquadsStore} from "data/stores/squads/squads.store";
import type {IRound, IRoundsStore} from "data/stores/rounds/rounds.store";
import type {IPredictionsStore} from "data/stores/predictions/predictions.store";
import type {IStaticContentStore, TArticle} from "data/stores/static_content/static_content.store";
import {AnalyticService} from "data/utils/track_analytics";
import {chain} from "lodash";

export interface IHomeController extends ViewController {
	readonly i18n: ILocalizationStore;

	get isAuthorized(): boolean;
	get isRequestSuccess(): boolean;
	get activeRound(): IRound | undefined;
	get contestsList(): IContest[];
	get faqContent(): TArticle[] | undefined;
}

@injectable()
export class HomeController implements IHomeController {
	@observable _requestState: RequestState = RequestState.IDLE;

	get isRequestSuccess() {
		return this._requestState === RequestState.SUCCESS;
	}
	get isAuthorized() {
		return this._userStore.isAuthorized;
	}

	get activeRound() {
		return this._roundsStore.activeRound;
	}

	get contestsList() {
		const activeRound = this._roundsStore.activeRound;

		if (!activeRound) return [];

		return chain(this._contestsStore.list)
			.filter((contest) => contest.roundId === activeRound.id)
			.orderBy(["gameStartDate"], ["asc"])
			.value();
	}

	get faqContent() {
		return this._staticContentStore.articles;
	}

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.RoundsStore) private _roundsStore: IRoundsStore,
		@inject(Bindings.ContestsStore) private _contestsStore: IContestsStore,
		@inject(Bindings.PlayersStore) private _playersStore: IPlayersStore,
		@inject(Bindings.SquadsStore) private _squadsStore: ISquadsStore,
		@inject(Bindings.PredictionsStore) private _predictionsStore: IPredictionsStore,
		@inject(Bindings.StaticContentStore)
		private readonly _staticContentStore: IStaticContentStore
	) {
		makeAutoObservable(this);
	}

	@action private setRequestState = (value: RequestState) => {
		this._requestState = value;
	};

	async init(param: void) {
		this.setRequestState(RequestState.PENDING);

		AnalyticService.trackState({
			"page.legacyName": "Major League Baseball: Player of the Game",
		});

		await Promise.all([
			this._roundsStore.fetchRounds(),
			this._contestsStore.fetchContest(),
			this._playersStore.fetchPlayers(),
			this._squadsStore.fetchSquads(),
			this._staticContentStore.fetchStaticContent(),
		])
			.then(() => {
				if (this._userStore.isAuthorized) {
					void this._predictionsStore.fetchAllPredictions();
				}
			})
			.finally(() => this.setRequestState(RequestState.SUCCESS));
	}

	dispose() {
		return;
	}
}
